import { Button } from '@avanta/shared/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@avanta/shared/components/ui/DropdownMenu';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronDown, LogOut, Settings, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { SupportButton } from 'components/SupportButton';
import { ROUTES } from 'constants/routes';
import { useUserStore } from 'stores/user';
import { useNavActions } from '../../hooks';

const SCHEDULE_BUTTON_SLOT_CLASS = 'h-8 min-w-36 shrink-0';

export const DesktopActions = (): React.ReactElement => {
  const navigate = useNavigate();
  const { greetingLabel, showScheduleButton, showSupportButton, handleProfile, handleLogout } =
    useNavActions();
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const user = useUserStore(state => state.user);

  const handleScheduleSession = (): void => {
    if (!user?.profileCompleteToBookAppointments) {
      navigate(ROUTES.APPOINTMENTS_PERSONAL_DATA);
      return;
    }
    navigate(ROUTES.APPOINTMENTS_RESERVE);
  };

  return (
    <div className="flex shrink-0 items-center gap-2">
      <div className="shrink-0">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="group mr-2 inline-flex items-center text-sm font-normal text-teal-600"
            >
              <User className="size-4 shrink-0 text-teal-600" aria-hidden />
              <span className="ml-1">{greetingLabel}</span>
              <ChevronDown
                className="ml-1 size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={4}
            className="w-52 data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[side=bottom]:slide-in-from-top-1 duration-200 ease-out"
          >
            <DropdownMenuItem onClick={handleProfile}>
              <div className="flex items-center gap-2">
                <Settings className="size-4 shrink-0 text-teal-600" aria-hidden />
                <span>{t('Nav.profile')}</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout}>
              <div className="flex items-center gap-2">
                <LogOut className="size-4 text-teal-600" />
                <span>{t('Nav.logout')}</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {showSupportButton && <SupportButton />}
      {showScheduleButton && (
        <Button
          size="lg"
          onClick={handleScheduleSession}
          className={cn(SCHEDULE_BUTTON_SLOT_CLASS, 'rounded-full px-6 text-sm font-medium')}
        >
          {t('AppointmentsBanner.cta', { ns: LOCIZE_NS.APPOINTMENTS })}
        </Button>
      )}
    </div>
  );
};
